import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronLeft,
  Compass,
  Flag,
  LayoutGrid,
  Map as MapIcon,
  MountainSnow,
  Route,
  Target,
  Footprints,
  Check,
} from "lucide-react";
import { fetchCollectionItems, fetchCollectionShareUrl, type CollectionItem } from "../api/collections";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import { ProgressRing } from "../components/ProgressRing";
import { StatBlock } from "../components/StatBlock";
import { CollectibleItem } from "../components/CollectibleItem";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { FeatureDetailOverlay } from "../components/map/FeatureDetailOverlay";
import { GoalButton } from "../components/GoalButton";
import { ShareCardButton } from "../components/ShareCardButton";
import { Skel } from "../components/Skeleton";
import { ImageCredit } from "../components/ImageCredit";
import { ProgressBar } from "../components/ProgressBar";
import { companionLabelText, type CollectionCompanion } from "@atlas/shared";
import type { MapFeatureProperties, FeatureKind } from "../api/map";
import { truncateAtWord } from "../lib/text";

const TYPE_ICON: Record<string, React.ReactNode> = {
  "Peak List": <MountainSnow size={30} />,
  peaks: <MountainSnow size={30} />,
  Peaks: <MountainSnow size={30} />,
  "National Trail": <Route size={30} />,
  trails: <Route size={30} />,
  Trails: <Route size={30} />,
  Landmarks: <Flag size={30} />,
  landmarks: <Flag size={30} />,
  Challenge: <CheckCircle2 size={30} />,
};

type TabFilter = "all" | "collected" | "remaining";

function kindForType(type: string): FeatureKind {
  if (type === "trails" || type === "National Trail" || type === "Trails") return "trail";
  if (type === "landmarks" || type === "Landmarks") return "landmark";
  return "peak";
}

function toMapFeature(item: CollectionItem, type: string): MapFeatureProperties {
  const kind = kindForType(type);
  const status = item.collected
    ? "collected"
    : (item.completedSections ?? 0) > 0
      ? "in_progress"
      : "not_collected";
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    type: kind,
    status,
    elevation: item.elevation ?? undefined,
    distanceKm: item.distanceKm ?? undefined,
    category: item.category ?? undefined,
  };
}

function itemHref(item: CollectionItem, type: string): string {
  const kind = kindForType(type);
  if (kind === "trail") return `/trails/${item.slug || item.id}`;
  if (kind === "landmark") return `/landmarks/${item.slug}`;
  return `/peaks/${item.slug}`;
}

function itemMeta(item: CollectionItem, type: string): React.ReactNode {
  if (type === "peaks" || type === "Peak List" || type === "Peaks") {
    return item.elevation != null ? (
      <React.Fragment>
        <b>{item.elevation}m</b>
        {item.visits > 0 && <span>×{item.visits}</span>}
      </React.Fragment>
    ) : null;
  }
  if (type === "landmarks" || type === "Landmarks") {
    return item.category ? <b>{item.category.replace(/_/g, " ")}</b> : null;
  }
  if (type === "trails" || type === "National Trail" || type === "Trails") {
    return (
      <React.Fragment>
        {item.distanceKm != null && <b>{item.distanceKm.toFixed(1)} km</b>}
        {item.totalSections != null && item.totalSections > 0 && (
          <span>{item.completedSections ?? 0}/{item.totalSections} sections</span>
        )}
      </React.Fragment>
    );
  }
  if (type === "regions") {
    return item.coveragePct != null ? (
      <b>{Math.round(item.coveragePct)}%</b>
    ) : null;
  }
  return null;
}

// Signed-out pitch (ads land on these pages): the empty 0% ring alone reads
// as a manual checklist, so say plainly that past walks fill it in.
type GuestKind = FeatureKind | "region";

const GUEST_COPY: Record<GuestKind, { question: string; noun: string; reached: string }> = {
  peak: { question: "Already climbed some of these", noun: "hills", reached: "every hill you reached" },
  trail: { question: "Already walked parts of these", noun: "trails", reached: "every section you covered" },
  landmark: { question: "Already been to some of these", noun: "landmarks", reached: "every landmark you reached" },
  region: { question: "Already explored some of these", noun: "areas", reached: "the ground you covered" },
};

function GuestConnectCard({
  kind,
  count,
  onSignup,
}: {
  kind: GuestKind;
  count: number;
  onSignup: () => void;
}) {
  const copy = GUEST_COPY[kind];
  return (
    <div
      className="r-wrap"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 20,
        padding: "22px 26px",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        flexShrink: 0,
      }}
    >
      <div className="r-basis-full" style={{ flex: 1, minWidth: 0 }}>
        <h2
          style={{
            font: "var(--type-h3)",
            color: "var(--text-primary)",
            margin: "0 0 6px",
          }}
        >
          {copy.question}
          {count > 0 ? ` ${count} ${copy.noun}` : ""}?
        </h2>
        <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 14.5, lineHeight: 1.55, maxWidth: 620 }}>
          Connect Strava, Apple Health or Health Connect, or drop in a GPX file. Atlas
          matches the walks you've already done and fills in {copy.reached}. Nothing to
          record, and the free plan covers your last 12 months.
        </p>
      </div>
      <Button variant="primary" onClick={onSignup}>
        Create a free account
      </Button>
    </div>
  );
}

/**
 * ATL-168: lists that sit beside this one (the Tops of a hill list). One
 * compact card per companion, plus the combined count the full round is
 * measured by (Donalds and tops, 141).
 */
function CompanionPanel({
  parentName,
  parentCompleted,
  parentCount,
  companions,
  showProgress,
  onOpen,
}: {
  parentName: string;
  parentCompleted: number;
  parentCount: number;
  companions: CollectionCompanion[];
  showProgress: boolean;
  onOpen: (slug: string) => void;
}) {
  const gap = (done: number, total: number) =>
    total - done > 0 ? `${done} / ${total} · ${total - done} to go` : `${done} / ${total} · complete`;
  return (
    <div id="companion-lists" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720, scrollMarginTop: 16 }}>
      {companions.map((k) => {
        const allDone = parentCompleted + k.completedCount;
        const allCount = parentCount + k.itemCount;
        return (
          <div
            key={k.slug}
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-sm)",
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
              <div className="eyebrow">{k.label}</div>
              <button
                type="button"
                onClick={() => onOpen(k.slug)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Open {k.name}
              </button>
            </div>
            {showProgress ? (
              <>
                <ProgressBar
                  value={k.completedCount}
                  max={Math.max(1, k.itemCount)}
                  label={k.name}
                  color="sky"
                  valueFormat={() => gap(k.completedCount, k.itemCount)}
                />
                <ProgressBar
                  value={allDone}
                  max={Math.max(1, allCount)}
                  label={`${parentName} and ${companionLabelText(k.label)}`}
                  size="sm"
                  color="sky"
                  valueFormat={() => gap(allDone, allCount)}
                />
              </>
            ) : (
              <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.55 }}>
                The {k.itemCount} {k.name} sit beside this list, {allCount} in all.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CollectionDetailPage({
  staticSlug,
}: {
  staticSlug?: string;
} = {}) {
  const params = useParams({ strict: false });
  const slug = staticSlug ?? (params as { slug?: string }).slug;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me, isLoading: authLoading } = useAuth();
  const isAuthenticated = !!me;
  // Signed-out visitors (search, ads) see a pitch where the stat strip would be.
  const isGuest = !authLoading && !me;

  const [tab, setTab] = React.useState<TabFilter>("all");
  const [drawerItem, setDrawerItem] = React.useState<CollectionItem | null>(null);

  // Full item set in one request - the API returns everything by default, and
  // every stat/tab count below assumes the complete collection (ATL-40).
  const itemsQuery = useQuery({
    queryKey: ["collections", slug, "items"],
    queryFn: () => fetchCollectionItems(slug!),
    enabled: !!slug,
  });

  const allItems = itemsQuery.data?.items ?? [];

  // A collection that is exactly one trail has nothing the trail page does
  // not show better; old links and the prerendered pages land here, so
  // forward them (ATL-132).
  const soleTrail =
    itemsQuery.data?.collection.type === "trails" && allItems.length === 1 ? allItems[0] : null;
  React.useEffect(() => {
    if (soleTrail) void navigate({ to: "/trails/$id", params: { id: soleTrail.slug || soleTrail.id }, replace: true });
  }, [soleTrail, navigate]);
  const isLoading = itemsQuery.isLoading;

  const collectionMeta = itemsQuery.data?.collection;
  const collectionType = collectionMeta?.type ?? "";

  // Prefer cached list data for progress stats (pct, itemCount, completedCount)
  const listCache = qc.getQueryData<{
    collections: Array<{
      id: string;
      slug: string;
      itemCount: number;
      completedCount: number;
      pct: number;
      name: string;
      type: string;
      coverImage: string | null;
      region: string | null;
      country: string | null;
    }>;
  }>(["collections"]);
  const listEntry = listCache?.collections.find((c) => c.slug === slug);

  const collectionId = listEntry?.id ?? collectionMeta?.id ?? null;
  const displayName = listEntry?.name ?? collectionMeta?.name ?? slug ?? "";
  const displayType = listEntry?.type ?? collectionType;
  const itemCount = listEntry?.itemCount ?? allItems.length;
  const completedCount = listEntry?.completedCount ?? allItems.filter((i) => i.collected).length;
  // Fractional progress: partially-walked trails and partially-covered
  // regions count pro-rata, so 2/9 sections reads 22%, not 0% (ATL-48).
  // Falls back to the cached list pct (same formula server-side) pre-load.
  const progressSum = allItems.reduce((s, i) => {
    if (i.collected) return s + 1;
    // Trails: server-computed fractional progress (partial sections
    // pro-rata); falls back to section counts for stale caches.
    if (i.progressPct != null) return s + Math.min(1, i.progressPct / 100);
    if (i.totalSections != null && i.totalSections > 0)
      return s + Math.min(1, (i.completedSections ?? 0) / i.totalSections);
    if (i.coveragePct != null) return s + Math.min(1, i.coveragePct / 100);
    return s;
  }, 0);
  const pct =
    allItems.length > 0
      ? (progressSum / allItems.length) * 100
      : (listEntry?.pct ?? 0);
  const coverImage = listEntry?.coverImage ?? collectionMeta?.cover_image ?? null;
  const regionLabel =
    listEntry?.region ??
    collectionMeta?.region ??
    listEntry?.country ??
    collectionMeta?.country ??
    "";
  const description = collectionMeta?.description ?? null;
  const hasDescription = !!description && description.trim().length > 0;

  // Stat aggregates from loaded items. Visits sum over ALL items — an
  // in-progress trail's completed sections are real visits even though the
  // trail isn't fully collected yet (ATL-48 showed "Total visits 0" with two
  // sections done).
  const collectedItems = allItems.filter((i) => i.collected);
  const totalVisits = allItems.reduce((s, i) => s + (i.visits ?? 0), 0);
  const highestElevation =
    collectedItems.length > 0
      ? Math.max(...collectedItems.map((i) => i.elevation ?? 0))
      : null;
  const firstItem = allItems[0] ?? null;
  const totalDistance =
    firstItem?.distanceKm != null
      ? allItems.reduce((s, i) => s + (i.distanceKm ?? 0), 0)
      : null;

  const remaining = itemCount - completedCount;

  // ATL-168: companion lists (absent from older cached responses).
  const parentList = itemsQuery.data?.parent ?? null;
  const companions = itemsQuery.data?.companions ?? [];

  // Stat card for 4th slot depends on collection type
  const fourthStat =
    displayType === "trails" || displayType === "National Trail" || displayType === "Trails"
      ? {
          label: "Total distance",
          value: totalDistance != null ? `${totalDistance.toFixed(0)} km` : "—",
          icon: <Route size={12} />,
        }
      : {
          label: "Highest",
          value:
            highestElevation != null && highestElevation > 0
              ? `${highestElevation}m`
              : "—",
          icon: <MountainSnow size={12} />,
        };

  // Filtered items for grid
  const shownItems =
    tab === "all"
      ? allItems
      : tab === "collected"
        ? collectedItems
        : allItems.filter((i) => !i.collected);

  const collectedCount = collectedItems.length;
  const remainingCount = allItems.filter((i) => !i.collected).length;

  const tabs: [TabFilter, string, number][] = [
    ["all", "All", allItems.length],
    ["collected", "Collected", collectedCount],
    ["remaining", "Remaining", remainingCount],
  ];

  const itemKind = kindForType(displayType);
  const itemKindPlural = `${itemKind}s`;
  const itemUrlPrefix = itemKind === "trail" ? "trails" : itemKind === "landmark" ? "landmarks" : "peaks";

  const fallbackDescription = `${displayName} is a checklist of ${itemCount} ${itemKindPlural}${regionLabel ? ` in ${regionLabel}` : ""}. Track your progress and see what's left to complete on Atlas.`;
  usePageMeta({
    title: `${displayName} - ${itemCount} ${itemKindPlural} checklist | Atlas`,
    description: hasDescription ? truncateAtWord(description as string, 200) : fallbackDescription,
    canonicalPath: `/collections/${slug}`,
    ogImage: coverImage ?? undefined,
    // Placeholder values ("<slug> - 0 peaks checklist") must never reach the
    // head - hold the prerendered tags until the collection has loaded.
    enabled: !isLoading && collectionMeta != null,
  });

  return (
    <div
      className="r-page"
      style={{
        padding: 28,
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {!isLoading && allItems.length > 0 && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: displayName,
            description: hasDescription ? description : fallbackDescription,
            url: `https://goatlas.co.uk/collections/${slug}`,
            itemListElement: allItems.slice(0, 20).map((item, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: item.name,
              url: `https://goatlas.co.uk/${itemUrlPrefix}/${item.slug}`,
            })),
          }}
        />
      )}

      {/* back nav: a companion list (ATL-168) goes back to its parent */}
      <button
        type="button"
        onClick={() =>
          void (parentList
            ? navigate({ to: "/collections/$slug", params: { slug: parentList.slug } })
            : navigate({ to: "/collections" }))
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> {parentList ? `Part of the ${parentList.name}` : "All collections"}
      </button>

      {/* hero banner */}
      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
          minHeight: 160,
          // overflow:hidden zeroes this flex child's automatic min-height, so
          // the scrollable column would squash it before scrolling kicks in
          flexShrink: 0,
        }}
      >
        {coverImage && (
          <img
            src={coverImage}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
        {coverImage && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(100deg, var(--bg-app) 18%, color-mix(in srgb, var(--bg-app) 60%, transparent) 44%, color-mix(in srgb, var(--bg-app) 12%, transparent) 100%), linear-gradient(0deg, color-mix(in srgb, var(--bg-app) 45%, transparent), transparent 60%)",
            }}
          />
        )}
        <ImageCredit src={coverImage} />

        <div
          className="r-wrap"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 28,
          }}
        >
          {isLoading ? (
            <div style={{ width: 132, height: 132, flexShrink: 0 }}>
              <Skel width="132px" height="132px" />
            </div>
          ) : (
            <ProgressRing
              value={pct}
              max={100}
              size={132}
              stroke={12}
              label="Collected"
            />
          )}

          <div className="r-basis-full" style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {displayType}
              {regionLabel ? ` · ${regionLabel}` : ""}
            </div>
            {isLoading ? (
              <Skel height="44px" width="240px" />
            ) : (
              <h1
                style={{
                  font: "var(--type-display)",
                  fontSize: 44,
                  color: "var(--text-primary)",
                  margin: "0 0 12px",
                  letterSpacing: "-.02em",
                }}
              >
                {displayName}
              </h1>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Badge variant="gold" dot>
                {completedCount} / {itemCount}
              </Badge>
              {remaining > 0 ? (
                <Badge variant="neutral">{remaining} to go</Badge>
              ) : (
                <Badge variant="success">Completed</Badge>
              )}
              <Badge variant="neutral">{Math.round(pct)}% complete</Badge>
              {/* ATL-168: the lists beside this one, jumping to their section */}
              {companions.map((k) => (
                <button
                  key={k.slug}
                  type="button"
                  onClick={() => document.getElementById("companion-lists")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  aria-label={`${k.name}: jump to section`}
                >
                  <Badge variant="info">
                    + {k.completedCount > 0 ? `${k.completedCount} / ${k.itemCount}` : k.itemCount} {companionLabelText(k.label)}
                  </Badge>
                </button>
              ))}
            </div>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}
          >
            {isAuthenticated && (
              <Button
                variant="primary"
                leftIcon={<MapIcon size={16} />}
                onClick={() =>
                  void navigate({
                    to: "/map",
                    search: { collection: slug },
                  })
                }
              >
                View on map
              </Button>
            )}
            {isAuthenticated ? (
              <>
                <GoalButton targetType="collection" targetId={collectionId} />
                {slug && (
                  <ShareCardButton
                    label="Share progress"
                    fetchUrl={() => fetchCollectionShareUrl(slug)}
                  />
                )}
              </>
            ) : (
              <Button variant="secondary" onClick={() => void navigate({ to: "/signup" })}>
                Sign up to track progress
              </Button>
            )}
            {isAuthenticated && displayType === "peaks" && slug && (
              <Button
                variant="ghost"
                leftIcon={<Compass size={16} />}
                onClick={() => void navigate({ to: "/where-to-start", search: { lists: slug } })}
              >
                Where to start
              </Button>
            )}
            <Button variant="ghost" leftIcon={<Route size={16} />} onClick={() => void navigate({ to: "/route-analyser" })}>
              Check a route against this
            </Button>
          </div>
        </div>
      </div>

      {hasDescription && (
        <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-md)", lineHeight: 1.65, maxWidth: 720 }}>
          {description}
        </p>
      )}

      {companions.length > 0 && (
        <CompanionPanel
          parentName={displayName}
          parentCompleted={completedCount}
          parentCount={itemCount}
          companions={companions}
          showProgress={isAuthenticated}
          onOpen={(s) => void navigate({ to: "/collections/$slug", params: { slug: s } })}
        />
      )}

      {isGuest && (
        <GuestConnectCard
          kind={displayType === "regions" ? "region" : itemKind}
          count={itemCount}
          onSignup={() => void navigate({ to: "/signup" })}
        />
      )}

      {/* stat strip: all zeros for a guest, so signed-in only */}
      {isAuthenticated && (
        <div
          className="r-cols-2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 16,
          }}
        >
          {[
            {
              label: "Collected",
              value: String(completedCount),
              icon: <CheckCircle2 size={12} />,
            },
            {
              label: "Remaining",
              value: String(remaining),
              icon: <Target size={12} />,
            },
            {
              label: "Total visits",
              value: String(totalVisits),
              icon: <Footprints size={12} />,
            },
            fourthStat,
          ].map(({ label, value, icon }) => (
            <div
              key={label}
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)",
                padding: 18,
                boxShadow: "var(--ring-top)",
              }}
            >
              {isLoading ? (
                <Skel height="56px" />
              ) : (
                <StatBlock label={label} value={value} icon={icon} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* tab bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: 4,
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-pill)",
          }}
        >
          {tabs.map(([id, label, count]) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "0 16px",
                  height: 34,
                  borderRadius: "var(--radius-pill)",
                  cursor: "pointer",
                  border: "none",
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 600,
                  background: active ? "var(--accent)" : "transparent",
                  color: active ? "var(--text-on-gold)" : "var(--text-secondary)",
                  boxShadow: active ? "var(--glow-gold-sm)" : "none",
                  transition: "var(--t-colors)",
                }}
              >
                {label}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: "var(--radius-pill)",
                    background: active ? "rgba(0,0,0,.16)" : "var(--surface-sunken)",
                    color: active ? "var(--text-on-gold)" : "var(--text-muted)",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pokédex grid */}
      {isLoading ? (
        <div
          className="r-cols-2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 12,
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="atlas-skel"
              style={{ aspectRatio: "1" }}
            />
          ))}
        </div>
      ) : (
        <div
          className="r-cols-2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 12,
          }}
        >
          {shownItems.map((item) => (
            <CollectibleItem
              key={item.id}
              name={item.name}
              collected={item.collected}
              image={item.photoUrl ?? null}
              meta={itemMeta(item, displayType)}
              icon={TYPE_ICON[displayType] ?? <LayoutGrid size={30} />}
              checkIcon={<Check size={13} />}
              href={itemHref(item, displayType)}
              onClick={(e) => {
                // Real anchors for the crawler (ATL-132). Trails always go to
                // their page; peaks and landmarks open the drawer when signed
                // in and go to the public page when signed out.
                e.preventDefault();
                const kind = kindForType(displayType);
                if (kind === "trail") {
                  void navigate({ to: "/trails/$id", params: { id: item.slug || item.id } });
                } else if (isAuthenticated) {
                  setDrawerItem(item);
                } else {
                  void navigate({ to: itemHref(item, displayType) });
                }
              }}
            />
          ))}
        </div>
      )}

      {drawerItem && (
        <FeatureDetailOverlay
          feature={toMapFeature(drawerItem, displayType)}
          onClose={() => setDrawerItem(null)}
          onShowOnMap={() => {
            const id = drawerItem.id;
            setDrawerItem(null);
            void navigate({ to: "/map", search: { collection: slug, focus: id } });
          }}
        />
      )}
    </div>
  );
}
